import { useState, useEffect, useCallback } from 'react';
import { MatrixEventEvent } from 'matrix-js-sdk';
import { useMatrixClient } from '../context/MatrixContext';

/**
 * Hook to get the E2EE shield status for all events in a room's timeline.
 * It returns a map from eventId to an EventEncryptionInfo object.
 *
 * @param {import('matrix-js-sdk').Room} room - The room object from the matrix-js-sdk.
 * @returns {Map<string, object>} A map where keys are event IDs and values are EventEncryptionInfo objects.
 */
export const useEventShields = (room) => {
  const client = useMatrixClient();
  const [eventShields, setEventShields] = useState(new Map());

  const calculateShields = useCallback(async () => {
    if (!client || !room || !client.getCrypto()) {
      setEventShields(new Map());
      return;
    }

    const newShields = new Map();
    const crypto = client.getCrypto();
    const events = room.getLiveTimeline().getEvents();

    await Promise.all(events.map(async (event) => {
      if (event.isEncrypted()) {
        const shield = await crypto.getEncryptionInfoForEvent(event);

        if (shield) {
          newShields.set(event.getId(), shield);
        }
      }
    }));

    setEventShields(newShields);
  }, [client, room]);

  useEffect(() => {
    if (!room || !client) return;

    calculateShields();

    // Re-calculate when an event is decrypted, as its shield status may change.
    const onEventDecrypted = (event) => {
      if (event.getRoomId() === room.roomId) {
        calculateShields();
      }
    };

    client.on(MatrixEventEvent.Decrypted, onEventDecrypted);

    return () => {
      client.off(MatrixEventEvent.Decrypted, onEventDecrypted);
    };
  }, [room, client, calculateShields]);

  return eventShields;
};